import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import type {DirectoryItem} from '@github-ui/code-view-types'
import {PortalTooltip} from '@github-ui/portal-tooltip/portalled'
import {useFileTreeTooltip} from '@github-ui/use-file-tree-tooltip'
import {useNavigate} from '@github-ui/use-navigate'
import {AlertFillIcon, FileIcon, FileSubmoduleIcon} from '@primer/octicons-react'
import {Spinner, TreeView, Checkbox} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import React, {useCallback, useEffect, useEffectEvent, useState, useReducer, use} from 'react'
import type {NavigateOptions, To} from '@github-ui/react-router'

import type {TreeItem} from '@github-ui/repos-file-tree-view'
import {useFetchFolder} from '../../hooks/use-fetch-folder'
import styles from './FileTreePicker.module.css'
import {sendEvent} from '@github-ui/hydro-analytics'
import {type FolderContents, KnownFoldersContext, resolveDirectoryItems} from './known-folders-context'

const LARGE_DIR_THRESHOLD = 1000

interface CommonProps {
  clientOnlyFilePaths?: string[]
  getItemUrl?: (item: DirectoryItem) => string
  onItemSelected?(item: DirectoryItem, treeItem: TreeItem<DirectoryItem>): void
  onRenderRow?(): void
  selectedItemRef?: React.Ref<HTMLElement>
  getFileTrailingVisual?: (item: DirectoryItem) =>
    | {
        trailingVisual: React.ReactElement
        screenReaderText?: string
      }
    | undefined
  getFileIcon?: (item: DirectoryItem) => React.ReactElement | null
  singleSelect?: boolean
}

interface FileTreePickerProps extends CommonProps {
  rootItems: Array<TreeItem<DirectoryItem>>
  setRootItems: (rootItems: Array<TreeItem<DirectoryItem>>) => void
  loading: boolean
  fetchError: boolean
  directoryNavigateOnClick: boolean
  navigateOnClick: boolean
  sortDirectoryItems?: (items: Array<TreeItem<DirectoryItem>>) => void
  selectedItems?: Set<string>
  onSelectionChange?: (items: Set<string>) => void
  expandedPath: string
  allowFolderReferences?: boolean
}

interface FileTreeRowProps extends CommonProps {
  isActive: boolean
  file: TreeItem<DirectoryItem>
  navigate: (to: To, options?: NavigateOptions) => void
  navigateOnClick: boolean
  isSelected?: boolean
}

interface DirectoryTreeRowProps extends CommonProps {
  directory: TreeItem<DirectoryItem>
  depth?: number
  isActive: boolean
  isAncestorOfActive: boolean
  leadingPath?: string
  navigate: (to: To, options?: NavigateOptions) => void
  navigateOnClick: boolean
  getFetchUrl: (item: DirectoryItem) => string
  itemCount?: number
  isSelected?: boolean
  selectedChildren: string[] // Changed this from selectedItems to selectedChildren
  expandedPath: string
}

interface DirectoryContentsProps extends CommonProps {
  directoryItems: Array<TreeItem<DirectoryItem>>
  leadingPath?: string
  inheritsActive?: boolean
  directoryNavigateOnClick: boolean
  navigateOnClick: boolean
  selectedChildren: string[]
  expandedPath: string
}

type SelectionAction =
  | {type: 'SINGLE_SELECT_FILE'; path: string}
  | {type: 'SELECT_FILE'; path: string}
  | {type: 'DESELECT_FILE'; path: string}
  | {type: 'SELECT_DIRECTORY'; paths: string[]}
  | {type: 'DESELECT_DIRECTORY'; paths: string[]}

function selectionReducer(state: Set<string>, action: SelectionAction): Set<string> {
  const newState = new Set(state)

  switch (action.type) {
    case 'SINGLE_SELECT_FILE':
      newState.clear()
      newState.add(action.path)
      sendEvent('copilot-spaces.click', {
        target: 'FILE_PICKER',
        action: 'SINGLE_SELECT_FILE',
        newFileCount: newState.size,
      })
      break
    case 'SELECT_FILE':
      newState.add(action.path)
      sendEvent('copilot-spaces.click', {target: 'FILE_PICKER', action: 'SELECT_FILE', newFileCount: newState.size})
      break
    case 'DESELECT_FILE':
      newState.delete(action.path)
      sendEvent('copilot-spaces.click', {target: 'FILE_PICKER', action: 'DESELECT_FILE', newFileCount: newState.size})
      break
    case 'SELECT_DIRECTORY':
      for (const path of action.paths) {
        newState.add(path)
      }
      sendEvent('copilot-spaces.click', {
        target: 'FILE_PICKER',
        action: 'SELECT_DIRECTORY',
        newFileCount: newState.size,
        directoryCount: action.paths.length,
      })
      break
    case 'DESELECT_DIRECTORY':
      for (const path of action.paths) {
        newState.delete(path)
      }
      sendEvent('copilot-spaces.click', {
        target: 'FILE_PICKER',
        action: 'DESELECT_DIRECTORY',
        newFileCount: newState.size,
        directoryCount: action.paths.length,
      })
      break
  }

  return newState
}

function WrappedFileTreeRow({
  isActive,
  file,
  onItemSelected,
  getItemUrl,
  selectedItemRef,
  navigate,
  onRenderRow,
  getFileTrailingVisual,
  getFileIcon,
  navigateOnClick,
  isSelected,
  singleSelect,
}: FileTreeRowProps & {isSelected?: boolean}) {
  const {sendRepoClickEvent} = useReposAnalytics()
  const rowRef = React.useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: rowRef, mouseRowRef: rowRef})
  const isSubModule = file.data.contentType === 'submodule'
  const trailingVisualData = getFileTrailingVisual?.(file.data)

  // Add useEffect for scrolling when the item becomes active
  useEffect(() => {
    if (isActive && rowRef.current) {
      // Use requestAnimationFrame to ensure the DOM has been updated
      requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({
          block: 'center',
        })
      })
    }
  }, [isActive])

  const onSelect = React.useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      if (isSubModule) {
        e.preventDefault()
        if (file.data.submoduleUrl) {
          window.location.href = file.data.submoduleUrl
        }
      } else {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.metaKey || e.ctrlKey || (e as React.MouseEvent<HTMLElement>).button === 1) {
          window.open(getItemUrl?.(file.data), '_blank')
          e.preventDefault()
        } else {
          onItemSelected?.(file.data, file)
          sendRepoClickEvent('FILES_TREE.ITEM', {['item_path']: file.data.path})
          if (navigateOnClick) {
            navigate(getItemUrl?.(file.data) || '')
          }
          e.stopPropagation()
        }
      }
    },
    [file, getItemUrl, isSubModule, navigate, navigateOnClick, onItemSelected, sendRepoClickEvent],
  )

  onRenderRow?.()

  return (
    <TreeView.Item
      ref={rowRef}
      onSelect={onSelect}
      current={isActive}
      id={`${file.data.path}-item`}
      containIntrinsicSize={!isActive ? 'auto 2rem' : undefined}
    >
      <TreeView.LeadingVisual>
        {getFileIcon ? getFileIcon(file.data) : isSubModule ? <FileSubmoduleIcon /> : <FileIcon />}
      </TreeView.LeadingVisual>
      <div className={styles.itemContainer}>
        {!singleSelect && (
          <Checkbox
            className={styles.checkbox}
            checked={isSelected || false}
            readOnly
            aria-label={`Select ${file.data.name}`}
          />
        )}
        <span
          ref={selectedItemRef}
          style={{color: isSubModule ? 'var(--fgColor-accent, var(--color-accent-fg))' : undefined}}
        >
          <span data-testid={`${file.data.path}-file-item`}>{file.data.name}</span>
        </span>
      </div>
      {showTooltip && (
        <PortalTooltip
          data-testid={`${file.data.name}-item-tooltip`}
          id={`${file.data.name}-item-tooltip`}
          contentRef={rowRef}
          aria-label={file.data.name}
          open
          direction="ne"
        />
      )}
      {!!trailingVisualData?.screenReaderText && <span className="sr-only">{trailingVisualData.screenReaderText}</span>}
      {!!trailingVisualData?.trailingVisual && (
        <TreeView.TrailingVisual>{trailingVisualData.trailingVisual}</TreeView.TrailingVisual>
      )}
    </TreeView.Item>
  )
}

export const FileTreeRow = React.memo(WrappedFileTreeRow)

// Add underscore prefix to mark as intentionally unused
function _getSelectedChildrenForPath(selectedItems: Set<string>, path: string): string[] {
  return Array.from(selectedItems).filter(item => item === path || item.startsWith(`${path}/`))
}

// Updated helper to check directory selection state based on selected children and totalCount
function getDirectorySelectionState(
  directory: TreeItem<DirectoryItem>,
  folders: ReadonlyMap<string, FolderContents>,
  selectedChildren?: string[],
  options?: {selectedSelf?: boolean; isLarge?: boolean},
): 'none' | 'partial' | 'all' {
  if (options?.selectedSelf && options?.isLarge) return 'all'

  if (!selectedChildren || selectedChildren.length === 0) return 'none'

  const selectedPathsSet = new Set(selectedChildren)

  // For large directories, if the directory itself is selected, it means all content is selected
  if (options?.isLarge && selectedPathsSet.has(directory.data.path)) {
    return 'all'
  }

  // Get all paths in this directory (all descendants) recursively, reading freshly fetched contents
  // from the KnownFolders map so a directory whose contents were loaded after the initial tree still
  // contributes its descendants to the selection calculation.
  const getAllPathsInDir = (dir: TreeItem<DirectoryItem>): Set<string> => {
    const paths = new Set<string>()

    const items = resolveDirectoryItems(dir, folders)
    // Add files (leaf nodes)
    for (const item of items) {
      if (item.data.contentType === 'directory') {
        const childPaths = getAllPathsInDir(item)
        for (const path of childPaths) {
          paths.add(path)
        }
      } else {
        paths.add(item.data.path)
      }
    }

    return paths
  }

  // Get all file paths (not directory paths) in this directory
  const allPossiblePaths = getAllPathsInDir(directory)

  // Check how many of the possible paths are selected
  const selectedCount = Array.from(allPossiblePaths).filter(path => selectedPathsSet.has(path)).length

  if (selectedCount === 0) return 'none'
  if (selectedCount === allPossiblePaths.size) return 'all'
  return 'partial'
}

// Add underscore prefix to mark as intentionally unused
function _getAllPathsInDirectory(directory: TreeItem<DirectoryItem>): string[] {
  const paths = [directory.data.path]
  for (const item of directory.items) {
    if (item.data.contentType === 'directory') {
      paths.push(..._getAllPathsInDirectory(item))
    } else {
      paths.push(item.data.path)
    }
  }
  return paths
}

const MemoizedCheckbox = React.memo(Checkbox)

export function WrappedDirectoryTreeRow({
  clientOnlyFilePaths,
  directory,
  isActive,
  isAncestorOfActive,
  leadingPath,
  onItemSelected,
  getItemUrl,
  getFetchUrl,
  selectedItemRef,
  navigate,
  onRenderRow,
  getFileTrailingVisual,
  getFileIcon,
  navigateOnClick,
  selectedChildren,
  expandedPath,
  singleSelect,
}: DirectoryTreeRowProps): React.ReactElement {
  // Move ALL hooks to the top
  const [isExpanded, setIsExpanded] = useState(isAncestorOfActive)
  const {sendRepoClickEvent} = useReposAnalytics()
  const rowRef = React.useRef<HTMLElement | null>(null)
  const listItemRef = React.useRef<HTMLElement>(null)
  const showTooltip = useFileTreeTooltip({focusRowRef: listItemRef, mouseRowRef: rowRef})

  const filteredChildren = React.useMemo(() => {
    if (!selectedChildren) return []

    // Get all paths that are either the directory itself or its descendants
    const childPaths = selectedChildren.filter(
      path => path === directory.data.path || path.startsWith(`${directory.data.path}/`),
    )

    return childPaths
  }, [selectedChildren, directory.data.path])

  const selectedSelf = React.useMemo(
    () => selectedChildren?.includes(directory.data.path) ?? false,
    [selectedChildren, directory.data.path],
  )

  const {folders, setFolder} = use(KnownFoldersContext)
  // Resolve the freshest known items for this directory. Falls back to the prop value until a fetch completes.
  // Selection logic (getDirectorySelectionState) reads this map recursively so subtrees that have been
  // fetched contribute their descendants even though the prop tree still shows them as empty.
  const directoryItems = resolveDirectoryItems(directory, folders)

  const isLargeDirectory = directoryItems.length > LARGE_DIR_THRESHOLD

  const selectionState = React.useMemo(
    () => getDirectorySelectionState(directory, folders, filteredChildren, {selectedSelf, isLarge: isLargeDirectory}),
    [directory, folders, filteredChildren, selectedSelf, isLargeDirectory],
  )

  const [fetchFolder, incrementallyShowItems, items, loading, error, clearError, _totalCount] = useFetchFolder(
    directory,
    setFolder,
    getFetchUrl,
  )

  const pathPrefix = leadingPath ? `${leadingPath}/` : ''
  const truncatedRows = _totalCount - items.length

  // Add useEffect for scrolling when the item becomes active
  useEffect(() => {
    if (isActive && rowRef.current) {
      // Use requestAnimationFrame to ensure the DOM has been updated
      requestAnimationFrame(() => {
        rowRef.current?.scrollIntoView({
          block: 'center',
        })
      })
    }
  }, [isActive])

  const onToggleExpanded = React.useCallback(
    (expanded: boolean) => {
      const willExpand = expanded && !isExpanded
      if (willExpand && !loading && !error) {
        if (directoryItems.length > 100) {
          incrementallyShowItems()
        }
      }
      if (expanded !== isExpanded) {
        setIsExpanded(expanded)
      }
    },
    [isExpanded, loading, error, directoryItems.length, incrementallyShowItems],
  )

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      if (
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        e.metaKey ||
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        e.ctrlKey ||
        ((e as React.MouseEvent<HTMLElement>).button === 1 && navigateOnClick)
      ) {
        window.open(getItemUrl?.(directory.data), '_blank')
        e.preventDefault()
      } else {
        if (singleSelect) {
          onToggleExpanded(!isExpanded)
        } else {
          onItemSelected?.(directory.data, directory)
          sendRepoClickEvent('FILES_TREE.ITEM', {['item_path']: directory.data.path})
          if (navigateOnClick) {
            navigate(getItemUrl?.(directory.data) || '')
          }
        }
        e.stopPropagation()
      }
    },
    [
      directory,
      getItemUrl,
      isExpanded,
      navigate,
      navigateOnClick,
      onItemSelected,
      onToggleExpanded,
      sendRepoClickEvent,
      singleSelect,
    ],
  )

  // Expand when becoming active, or being an ancestor of the active path
  const expandIfAncestor = useEffectEvent(() => {
    if (!isExpanded) {
      onToggleExpanded?.(true)
    }
  })
  React.useEffect(() => {
    if (isAncestorOfActive) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      expandIfAncestor()
    }
  }, [isAncestorOfActive])

  // Collapse when all items have been removed.
  const handleItemsLengthChange = useEffectEvent(() => {
    if (directoryItems.length === 0 && isExpanded) {
      onToggleExpanded?.(false)
    } else if (!isExpanded && directory.autoExpand) {
      onToggleExpanded?.(true)
    }
  })
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleItemsLengthChange()
  }, [directoryItems.length])

  const setRowRef = React.useCallback(
    (refElem: HTMLElement) => {
      if (selectedItemRef && isActive) {
        ;(selectedItemRef as (item: HTMLElement) => void)(refElem)
      }
      rowRef.current = refElem
    },
    [selectedItemRef, isActive],
  )

  // Early return with proper child selection state
  if (directoryItems.length === 1) {
    const firstItem = directoryItems[0]
    if (firstItem && firstItem.data.contentType === 'directory') {
      return (
        <DirectoryContents
          clientOnlyFilePaths={clientOnlyFilePaths}
          directoryItems={directoryItems}
          leadingPath={pathPrefix + directory.data.name}
          inheritsActive={isActive}
          onItemSelected={onItemSelected}
          selectedItemRef={selectedItemRef}
          getItemUrl={getItemUrl}
          directoryNavigateOnClick={navigateOnClick}
          navigateOnClick={navigateOnClick}
          getFileTrailingVisual={getFileTrailingVisual}
          getFileIcon={getFileIcon}
          selectedChildren={filteredChildren}
          expandedPath={expandedPath}
          singleSelect={singleSelect}
        />
      )
    }
  }

  onRenderRow?.()

  return (
    <TreeView.Item
      ref={listItemRef}
      expanded={isExpanded}
      onExpandedChange={onToggleExpanded}
      current={isActive}
      onSelect={onClick}
      id={`${directory.data.path}-item`}
      containIntrinsicSize={!isActive ? 'auto 2rem' : undefined}
    >
      <TreeView.LeadingVisual>
        <TreeView.DirectoryIcon />
      </TreeView.LeadingVisual>
      <div className={styles.itemContainer}>
        {!singleSelect && (
          <MemoizedCheckbox
            className={styles.checkbox}
            checked={selectionState === 'all'}
            indeterminate={selectionState === 'partial'}
            readOnly
            aria-label={`Select ${directory.data.name}`}
            data-indeterminate={selectionState === 'partial' ? 'true' : undefined}
          />
        )}
        <span ref={setRowRef}>
          {pathPrefix && <span>{pathPrefix}</span>}
          <span data-testid={`${directory.data.path}-directory-item`}>{directory.data.name}</span>
        </span>
      </div>
      {showTooltip && (
        <PortalTooltip
          data-testid={`${directory.data.name}-directory-item-tooltip`}
          id={`${directory.data.name}-directory-item-tooltip`}
          contentRef={listItemRef}
          aria-label={`${pathPrefix}${directory.data.name}`}
          open
          direction="ne"
        />
      )}

      <TreeView.SubTree state={loading ? 'loading' : error ? 'error' : 'done'}>
        {error ? (
          <TreeView.ErrorDialog onRetry={fetchFolder} onDismiss={clearError}>
            There was an error loading the folder contents.
          </TreeView.ErrorDialog>
        ) : (
          <>
            <DirectoryContents
              clientOnlyFilePaths={clientOnlyFilePaths}
              directoryItems={items}
              onItemSelected={onItemSelected}
              selectedItemRef={selectedItemRef}
              getItemUrl={getItemUrl}
              directoryNavigateOnClick={navigateOnClick}
              navigateOnClick={navigateOnClick}
              getFileTrailingVisual={getFileTrailingVisual}
              getFileIcon={getFileIcon}
              selectedChildren={filteredChildren}
              expandedPath={expandedPath}
              singleSelect={singleSelect}
            />
            {truncatedRows > 0 && <ErrorTreeRow message={`${truncatedRows} entries not shown`} />}
          </>
        )}
      </TreeView.SubTree>
    </TreeView.Item>
  )
}

// Make sure DirectoryTreeRow is properly memoized
const DirectoryTreeRow = React.memo(WrappedDirectoryTreeRow, (prevProps, nextProps) => {
  // Custom comparison function for more granular control over rerenders
  return (
    prevProps.isActive === nextProps.isActive &&
    prevProps.isAncestorOfActive === nextProps.isAncestorOfActive &&
    prevProps.directory === nextProps.directory &&
    (prevProps.selectedChildren === nextProps.selectedChildren ||
      (prevProps.selectedChildren.length === 0 && nextProps.selectedChildren.length === 0)) &&
    prevProps.expandedPath === nextProps.expandedPath
  )
})

function WrappedDirectoryContents({
  clientOnlyFilePaths,
  directoryItems,
  leadingPath,
  inheritsActive,
  onItemSelected,
  selectedItemRef,
  onRenderRow,
  getItemUrl,
  getFileTrailingVisual,
  getFileIcon,
  directoryNavigateOnClick,
  navigateOnClick,
  selectedChildren,
  expandedPath,
  singleSelect,
}: DirectoryContentsProps): React.ReactElement {
  const urlCreator = useUrlCreator()
  const navigate = useNavigate()
  const navigateRef = React.useRef(navigate)
  const onItemSelectedRef = React.useRef(onItemSelected)

  // Update ref when deps change
  React.useEffect(() => {
    onItemSelectedRef.current = onItemSelected
    navigateRef.current = navigate
  }, [onItemSelected, navigate])

  const handleItemSelection = React.useCallback((itemData: DirectoryItem, item: TreeItem<DirectoryItem>) => {
    onItemSelectedRef.current?.(itemData, item)
  }, [])

  // Memoize the rendered items to avoid recalculating on every render
  const renderedItems = React.useMemo(
    () =>
      directoryItems.map(item => {
        const isActive = expandedPath === item.data.path
        const isSelected = selectedChildren?.includes(item.data.path)
        const isAncestorOfActive = isActive || expandedPath.startsWith(`${item.data.path}/`)

        const itemSelectedChildren =
          selectedChildren?.filter(path => path === item.data.path || path.startsWith(`${item.data.path}/`)) || []

        if (item.data.contentType === 'directory') {
          return (
            <DirectoryTreeRow
              key={item.data.name}
              clientOnlyFilePaths={clientOnlyFilePaths}
              isActive={inheritsActive || isActive}
              isAncestorOfActive={isAncestorOfActive}
              onItemSelected={handleItemSelection}
              leadingPath={leadingPath}
              directory={item}
              getItemUrl={getItemUrl}
              getFetchUrl={urlCreator.getItemUrl}
              selectedItemRef={isAncestorOfActive ? selectedItemRef : undefined}
              navigate={navigate}
              onRenderRow={onRenderRow}
              navigateOnClick={directoryNavigateOnClick}
              getFileTrailingVisual={getFileTrailingVisual}
              getFileIcon={getFileIcon}
              itemCount={item.items.length}
              isSelected={isSelected}
              selectedChildren={itemSelectedChildren}
              expandedPath={expandedPath}
              singleSelect={singleSelect}
            />
          )
        }

        return (
          <FileTreeRow
            key={item.data.name}
            onItemSelected={handleItemSelection}
            file={item}
            isActive={isActive}
            getItemUrl={getItemUrl}
            selectedItemRef={isActive ? selectedItemRef : undefined}
            navigate={navigate}
            navigateOnClick={navigateOnClick}
            onRenderRow={onRenderRow}
            getFileTrailingVisual={getFileTrailingVisual}
            getFileIcon={getFileIcon}
            isSelected={isSelected}
            singleSelect={singleSelect}
          />
        )
      }),
    [
      directoryItems,
      expandedPath,
      selectedChildren,
      handleItemSelection,
      getItemUrl,
      selectedItemRef,
      navigate,
      navigateOnClick,
      onRenderRow,
      getFileTrailingVisual,
      getFileIcon,
      singleSelect,
      clientOnlyFilePaths,
      inheritsActive,
      leadingPath,
      urlCreator.getItemUrl,
      directoryNavigateOnClick,
    ],
  )

  return <>{renderedItems}</>
}

const DirectoryContents = React.memo(WrappedDirectoryContents)

// Module-scope so it has a stable identity for `useCallback` deps and so the recursive self-reference
// doesn't trip the React Compiler's immutability check on closure-captured callbacks.
function getAllFilePaths(item: TreeItem<DirectoryItem>): string[] {
  const pathsSet = new Set<string>()
  pathsSet.add(item.data.path)

  if (item.data.contentType === 'directory') {
    for (const child of item.items) {
      const childPaths = getAllFilePaths(child)
      for (const path of childPaths) {
        pathsSet.add(path)
      }
    }
  }

  return Array.from(pathsSet)
}

export function FileTreePicker(props: FileTreePickerProps) {
  const {
    clientOnlyFilePaths,
    rootItems,
    fetchError,
    loading,
    onRenderRow,
    getItemUrl,
    getFileTrailingVisual,
    getFileIcon,
    expandedPath,
    onSelectionChange,
  } = props

  const [internalSelectedItems, dispatch] = useReducer(selectionReducer, new Set<string>())
  const selectedItems = props.selectedItems || internalSelectedItems

  // Per-path cache of folder contents fetched at runtime. Replaces the previous prop-mutation
  // pattern: `useFetchFolder` writes here via `setFolder`, and `WrappedDirectoryTreeRow` /
  // `getDirectorySelectionState` read freshly fetched items from here instead of mutated props.
  const [folders, setFolders] = useState<ReadonlyMap<string, FolderContents>>(() => new Map())
  const setFolder = useCallback((path: string, items: Array<TreeItem<DirectoryItem>>, totalCount: number) => {
    setFolders(prev => {
      const next = new Map(prev)
      next.set(path, {items, totalCount})
      return next
    })
  }, [])
  const knownFoldersValue = React.useMemo(() => ({folders, setFolder}), [folders, setFolder])

  // Helper function to determine selection action
  function getSelectionAction(singleSelect: boolean, isSelected: boolean, path: string) {
    if (singleSelect) {
      return {type: 'SINGLE_SELECT_FILE' as const, path}
    }
    if (isSelected) {
      return {type: 'DESELECT_FILE' as const, path}
    }
    return {type: 'SELECT_FILE' as const, path}
  }

  // Create stable action dispatchers
  const handleFileSelection = useCallback(
    (path: string, isSelected: boolean) => {
      const action = getSelectionAction(!!props.singleSelect, isSelected, path)

      if (onSelectionChange) {
        const newState = selectionReducer(selectedItems, action)
        onSelectionChange(newState)
      } else {
        dispatch(action)
      }
    },
    [onSelectionChange, props.singleSelect, selectedItems],
  )

  const handleDirectorySelection = useCallback(
    (treeItem: TreeItem<DirectoryItem>, isFullySelected: boolean) => {
      const isLarge = !!props.allowFolderReferences && treeItem.items.length > LARGE_DIR_THRESHOLD

      if (isLarge) {
        const dirPath = treeItem.data.path
        const action = selectedItems.has(dirPath)
          ? {type: 'DESELECT_FILE' as const, path: dirPath}
          : {type: 'SELECT_FILE' as const, path: dirPath}

        if (onSelectionChange) {
          const newState = selectionReducer(selectedItems, action)
          onSelectionChange(newState)
        } else {
          dispatch(action)
        }

        return
      }

      const paths = getAllFilePaths(treeItem)
      const action = isFullySelected
        ? {type: 'DESELECT_DIRECTORY' as const, paths}
        : {type: 'SELECT_DIRECTORY' as const, paths}

      if (onSelectionChange) {
        const newState = selectionReducer(selectedItems, action)
        onSelectionChange(newState)
      } else {
        dispatch(action)
      }
    },
    [onSelectionChange, selectedItems, props.allowFolderReferences],
  )

  // Main item selection handler that delegates to appropriate selection handler
  const handleItemSelected = useCallback(
    (item: DirectoryItem, treeItem: TreeItem<DirectoryItem>) => {
      if (item.contentType === 'directory') {
        const isLarge = !!props.allowFolderReferences && treeItem.items.length > LARGE_DIR_THRESHOLD
        if (isLarge) {
          const dirSelected = selectedItems.has(item.path)
          handleDirectorySelection(treeItem, dirSelected)
          return
        }

        const paths = getAllFilePaths(treeItem)
        const selectedPathsInThisDirectory = paths.filter(path => selectedItems.has(path))
        const hasAnySelected = selectedPathsInThisDirectory.length > 0
        const isFullySelected = paths.length === selectedPathsInThisDirectory.length

        if (hasAnySelected && !isFullySelected) {
          // Deselect all currently selected paths in this directory in case of partial selection
          handleDirectorySelection(treeItem, true) // true means "currently selected", so this will deselect
        } else {
          // Normal toggle behavior for fully selected or unselected directories
          handleDirectorySelection(treeItem, isFullySelected)
        }
      } else {
        handleFileSelection(item.path, selectedItems.has(item.path))
      }
    },
    [handleDirectorySelection, handleFileSelection, selectedItems, props.allowFolderReferences],
  )

  const onMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 1) {
      event.preventDefault()
    }
  }, [])

  return (
    <KnownFoldersContext value={knownFoldersValue}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onMouseDown={onMouseDown} data-testid="repos-file-tree-container" className={styles.Box}>
        {loading ? (
          <div className={styles.Box_1}>
            <Spinner aria-label="Loading file tree" />
            <span className="fgColor-muted">Fetching files...</span>
          </div>
        ) : (
          <nav aria-label="File Tree Navigation">
            <TreeView aria-label="Files">
              {fetchError && <ErrorTreeRow message="Some files could not be loaded." />}
              <DirectoryContents
                clientOnlyFilePaths={clientOnlyFilePaths}
                directoryItems={rootItems}
                onItemSelected={handleItemSelected}
                // Convert selectedItems to selectedChildren for the root level
                selectedChildren={selectedItems ? Array.from(selectedItems) : []}
                selectedItemRef={props.selectedItemRef}
                onRenderRow={onRenderRow}
                directoryNavigateOnClick={props.directoryNavigateOnClick}
                navigateOnClick={props.navigateOnClick}
                getItemUrl={getItemUrl}
                getFileTrailingVisual={getFileTrailingVisual}
                getFileIcon={getFileIcon}
                expandedPath={expandedPath}
                singleSelect={props.singleSelect}
              />
            </TreeView>
          </nav>
        )}
      </div>
    </KnownFoldersContext>
  )
}

function ErrorTreeRow({message}: {message?: string}) {
  const errorMessage = message || "Couldn't load."
  return (
    <TreeView.Item id="error-tree-row">
      <TreeView.LeadingVisual>
        <Octicon icon={AlertFillIcon} className={styles.Octicon} />
      </TreeView.LeadingVisual>
      <div className={styles.Box_2}>{errorMessage}</div>
    </TreeView.Item>
  )
}
